import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import Pagination from '../components/common/Pagination';

// Cada vista de Producción es un ítem propio del menú lateral (ver config/navigation.js)
export const PRODUCTION_VIEWS = {
  in_progress: { index: 0, title: 'En Proceso', path: '/production' },
  billing: { index: 1, title: 'Para Facturar', path: '/production/billing' },
  delivered: { index: 2, title: 'Entregados', path: '/production/delivered' },
};

const Production = ({ view = 'in_progress' }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const currentView = PRODUCTION_VIEWS[view] || PRODUCTION_VIEWS.in_progress;
  const tabValue = currentView.index;

  // Devolver pedido authentication state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // Pagination states
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const fetchOrders = React.useCallback(async (currentPage = page) => {
    try {
      setLoading(true);

      // Cada pestaña se resuelve con UNA sola llamada paginada. Los estados
      // combinados usan status_in para que el total y la paginación sean correctos
      // (antes se hacían 2 llamadas y se sumaban los totales, lo que ocultaba
      // pedidos cuando un estado superaba el tamaño de página).
      const STATUS_QUERY_BY_TAB = {
        0: 'status_in=confirmado,en_elaboracion', // En Proceso
        1: 'status=listo',                        // Para Facturar
        // Los pedidos facturados se entregan desde los listados de pedidos (/orders/*/list)
        2: 'status_in=entregado,devuelto',        // Entregados y Devoluciones
      };
      const statusQuery = STATUS_QUERY_BY_TAB[tabValue] || STATUS_QUERY_BY_TAB[0];

      const response = await axios.get(`/api/orders/?${statusQuery}&page=${currentPage + 1}`);
      let allOrders = response.data.results || response.data;
      const calculatedTotal = response.data.count ?? allOrders.length;

      // Combinar y ordenar por fecha de entrega (más próxima primero), luego por fecha de creación
      allOrders.sort((a, b) => {
        // Si ambos tienen fecha de entrega, ordenar por fecha de entrega (más próxima primero)
        if (a.delivery_date && b.delivery_date) {
          const dateA = new Date(a.delivery_date);
          const dateB = new Date(b.delivery_date);
          // Si las fechas son iguales, ordenar por fecha de creación (más reciente primero)
          if (dateA.getTime() === dateB.getTime()) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          return dateA - dateB; // Ascendente: más próxima primero
        }
        // Si solo uno tiene fecha de entrega, ese va primero
        if (a.delivery_date && !b.delivery_date) return -1;
        if (!a.delivery_date && b.delivery_date) return 1;
        // Si ninguno tiene fecha de entrega, ordenar por fecha de creación (más reciente primero)
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setOrders(allOrders);
      setTotalCount(calculatedTotal);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  }, [tabValue]);

  useEffect(() => {
    fetchOrders(page);
    const interval = setInterval(() => fetchOrders(page), 30000);
    return () => clearInterval(interval);
  }, [fetchOrders, page]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmado: { bg: '#e3f2fd', color: '#1976d2', label: 'Confirmado' },
      en_elaboracion: { bg: '#fff3e0', color: '#f57c00', label: 'En Elaboración' },
      listo: { bg: '#e8f5e9', color: '#2e7d32', label: 'Listo' },
      facturado: { bg: '#e0f7fa', color: '#00838f', label: 'Facturado' },
      entregado: { bg: '#e0e0e0', color: '#616161', label: 'Entregado' },
      devuelto: { bg: '#fce4ec', color: '#c2185b', label: 'Devolución' },
    };
    return colors[status] || { bg: '#f5f5f5', color: '#616161', label: status };
  };



  const formatDate = (order) => {
    // Usar validated_at si existe (cuando fue confirmado), sino updated_at, sino created_at
    const dateString = order.validated_at || order.updated_at || order.created_at;
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return 'Ahora';
    } else if (diffMins < 60) {
      return `Hace ${diffMins} min`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} h`;
    } else {
      return `Hace ${diffDays} d`;
    }
  };

  const handleReturnClick = (e, order) => {
    e.stopPropagation();
    setSelectedOrder(order);
    setReturnDialogOpen(true);
    setAuthError('');
    setAuthUsername('');
    setAuthPassword('');
  };

  const handleReturnOrder = async () => {
    if (!authUsername || !authPassword) {
      setAuthError('Usuario y contraseña son requeridos');
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      await axios.post(`/api/orders/${selectedOrder.id}/return_order/`, {
        username: authUsername,
        password: authPassword
      });
      setReturnDialogOpen(false);
      fetchOrders(); // Refresh list
    } catch (err) {
      setAuthError(err.response?.data?.error || 'Error al procesar la devolución');
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: 3, pt: { xs: 7, md: 3 }, bgcolor: '#f6f7f8' }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                color: '#0e141b',
                fontSize: { xs: '1.875rem', md: '2.25rem' },
                fontWeight: 900,
                letterSpacing: '-0.033em',
              }}
            >
              {currentView.title}
            </Typography>
          </Box>
          <Typography sx={{ color: '#4e7397' }}>
            Producción · {totalCount} {totalCount === 1 ? 'pedido' : 'pedidos'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ReceiptLongIcon sx={{ fontSize: '3rem', color: '#cbd5e0' }} />
              <Typography sx={{ color: '#4e7397' }}>
                {tabValue === 0 ? 'No hay pedidos en producción' : tabValue === 1 ? 'No hay pedidos para facturar' : 'No hay pedidos entregados recientemente'}
              </Typography>
            </Box>
          </Paper>
        ) : (
          <>
            {/* Desktop Table */}
            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f6f7f8' }}>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', py: 2 }}>
                      Pedido
                    </TableCell>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', minWidth: 200 }}>
                      Cliente
                    </TableCell>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                      Items
                    </TableCell>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                      Estado
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>
                      Acción
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => {
                    const statusColor = getStatusColor(order.status);
                    const client = order.client;
                    const clientName = (client && client.name) ? client.name : 'Consumidor Final';
                    const isWholesale = order.order_type === 'wholesale';

                    return (
                      <TableRow
                        key={order.id}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&:hover': { 
                            bgcolor: '#f8fafc', 
                            cursor: 'pointer',
                            boxShadow: 'inset 4px 0 0 #800020',
                          },
                          '& .MuiTableCell-root': { borderColor: '#e2e8f0', py: 1.5 },
                        }}
                        onClick={() => navigate(`/production/${order.id}`)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                              width: 36, height: 36, borderRadius: '0.5rem',
                              bgcolor: order.special_production ? '#fff3e0' : '#e3f2fd',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <ReceiptLongIcon sx={{ color: order.special_production ? '#f57c00' : '#1976d2', fontSize: '1.2rem' }} />
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#0e141b' }}>#{order.order_number}</Typography>
                                <Chip 
                                  label={isWholesale ? 'Negocio' : 'Público'} 
                                  size="small" 
                                  sx={{ 
                                    height: 18, 
                                    fontSize: '0.65rem', 
                                    fontWeight: 700, 
                                    bgcolor: isWholesale ? '#ede7f6' : '#e0f7fa', 
                                    color: isWholesale ? '#5e35b1' : '#00838f',
                                    borderRadius: '4px'
                                  }} 
                                  />
                              </Box>
                              <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{formatDate(order)}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0e141b' }}>{clientName}</Typography>
                          {client?.delivery_address && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{client.delivery_address}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{order.item_count} items</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>${parseFloat(order.total || 0).toLocaleString('es-AR')}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={statusColor.label} size="small" sx={{ bgcolor: statusColor.bg, color: statusColor.color, fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {tabValue === 2 && order.status === 'entregado' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={(e) => handleReturnClick(e, order)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Devolver
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (tabValue === 1) navigate(`/billing/${order.id}`);
                                else navigate(`/production/${order.id}`);
                              }}
                              sx={{ bgcolor: '#1976d2', textTransform: 'none', fontWeight: 600 }}
                            >
                              {tabValue === 0 ? 'Procesar' : tabValue === 1 ? 'Facturar' : 'Ver'}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Cards */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 2 }}>
              {orders.map((order) => {
                const statusColor = getStatusColor(order.status);
                const client = order.client;
                const clientName = (client && client.name) ? client.name : 'Consumidor Final';
                const isWholesale = order.order_type === 'wholesale';

                return (
                  <Paper
                    key={order.id}
                    onClick={() => navigate(`/production/${order.id}`)}
                    sx={{
                      p: 2,
                      borderRadius: '1rem',
                      border: '1px solid #d0dbe7',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      '&:active': { bgcolor: '#f6f7f8' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '0.75rem',
                          bgcolor: order.special_production ? '#fff3e0' : '#e3f2fd',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <ReceiptLongIcon sx={{ color: order.special_production ? '#f57c00' : '#1976d2' }} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 800, color: '#0e141b' }}>#{order.order_number}</Typography>
                            <Chip 
                              label={isWholesale ? 'Negocio' : 'Público'} 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem', 
                                fontWeight: 700, 
                                bgcolor: isWholesale ? '#ede7f6' : '#e0f7fa', 
                                color: isWholesale ? '#5e35b1' : '#00838f',
                                borderRadius: '4px'
                              }} 
                            />
                          </Box>
                          <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{formatDate(order)}</Typography>
                        </Box>
                      </Box>
                      <Chip label={statusColor.label} size="small" sx={{ bgcolor: statusColor.bg, color: statusColor.color, fontWeight: 700, fontSize: '0.65rem' }} />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#4e7397', mb: 0.5 }}>Cliente</Typography>
                      <Typography sx={{ fontWeight: 700, color: '#0e141b' }}>{clientName}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pt: 2, borderTop: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{order.item_count} items</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#1b5e20', fontSize: '1.1rem' }}>${parseFloat(order.total || 0).toLocaleString('es-AR')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {tabValue === 2 && order.status === 'entregado' && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={(e) => handleReturnClick(e, order)}
                            sx={{ borderRadius: '0.5rem', textTransform: 'none', fontWeight: 700 }}
                          >
                            Devolver
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tabValue === 1) navigate(`/billing/${order.id}`);
                            else navigate(`/production/${order.id}`);
                          }}
                          sx={{ bgcolor: '#1976d2', borderRadius: '0.5rem', textTransform: 'none', fontWeight: 700 }}
                        >
                          {tabValue === 0 ? 'Procesar' : tabValue === 1 ? 'Facturar' : 'Ver'}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
            {totalCount > 0 && (
              <Pagination
                count={totalCount}
                page={page}
                rowsPerPage={20}
                onPageChange={(e, newPage) => setPage(newPage)}
              />
            )}
          </>
        )}
      </Box>

      {/* Return Order Authentication Dialog */}
      <Dialog open={returnDialogOpen} onClose={() => !authLoading && setReturnDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Marcar como Devuelto</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#4e7397' }}>
            Está a punto de marcar el pedido <strong>#{selectedOrder?.order_number}</strong> como devuelto.
            Por seguridad, ingrese cuenta y contraseña del responsable.
          </Typography>

          {authError && (
            <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Cuenta (Usuario)"
              fullWidth
              value={authUsername}
              onChange={(e) => setAuthUsername(e.target.value)}
              disabled={authLoading}
              autoFocus
            />
            <TextField
              label="Contraseña"
              type="password"
              fullWidth
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              disabled={authLoading}
              onKeyPress={(e) => e.key === 'Enter' && handleReturnOrder()}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setReturnDialogOpen(false)} disabled={authLoading} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            onClick={handleReturnOrder}
            variant="contained"
            color="error"
            disabled={authLoading || !authUsername || !authPassword}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {authLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Devolución'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Production;
